<template>
  <div class="service-dashboard">

    <div class="search-bar" style="float: right">
      <i class="pi pi-search" />
      <InputText v-model="filters['global'].value" :placeholder="$t('service.appliances.search')" style="width:70px" @update:modelValue="freeSearch" :aria-label="$t('service.appliances.search')"/>
      <i class="pi pi-times" @click="() => { filters['global'].value = null; freeSearch(); }"/>
    </div>
    <div class="p-formgroup-inline p-ai-center">
      <div class="p-field">
        <Dropdown id="modelNumber" v-model="filters['modelNumber'].value" :options="modelNumbers" optionLabel="name" optionValue="code"
          :placeholder="$t('service.appliances.filter_model_ph')" @change="changeFilters" />
      </div>
      <div class="p-field">
        <ToggleButton v-model="statusButton" :onLabel="$t('service.appliances.filter_all')" :offLabel="$t('service.appliances.filter_online')" @change="changeFilters" :aria-label="$t('common.online')+'/'+$t('common.offline')" />      
      </div>
    </div>

    <DataTable :value="appliances" stripedRows 
      v-model:filters="filters" responsiveLayout="stack" 
      breakpoint="960px" sortField="serialNumber" :sortOrder="1"
      :paginator="true" paginatorPosition="bottom" paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink"
      v-model:first="table.first" :rows="table.rows" :totalRecords="table.totalRecords" :lazy="true"
      @update:first="reloadAppliances"
      :currentPageReportTemplate="'{totalRecords} ' + $t('service.appliances.counter')">
      <template #empty>
          {{$t('service.appliances.empty_list')}}
      </template>

      <Column field="serialNumber" :header="$t('service.appliances.col_serialNumber')" ></Column>

      <Column field="modelNumber" :header="$t('service.appliances.col_model')" >
        <template #body="slotProps">
          <div>
            {{$functions.fixModelName(slotProps.data.modelNumber)}} 
            <img v-if="slotProps.data.online" src="assets/irinox/icons/status_online_small.svg" class="p-pl-1"  :alt="$t('common.online')"/>
            <img v-else src="assets/irinox/icons/status_offline_small.svg" class="p-pl-1"  :alt="$t('common.offline')"/>
          </div>
        </template>
      </Column>
      
      <Column :header="$t('service.appliances.col_notices')" >
        <template #body="slotProps">
          <div class="button-notices">
            <img v-if="slotProps.data.alarms!=null && slotProps.data.alarms.length>0" src="assets/irinox/icons/alert.svg" v-tooltip.left="$functions.alarmTooltip(slotProps.data.alarms, $t)" :alt="$t('common.alarm')"/>
            <img v-if="slotProps.data.anomalies!=null && slotProps.data.anomalies.length>0 && $config.maintenanceEnabled()" src="assets/irinox/icons/maintenance.svg" v-tooltip.left="$t('dashboard.anomaly_tooltip')" :alt="$t('ommon.anomaly')"/>
          </div>
        </template>
      </Column>
      
      <Column field="name" :header="$t('service.appliances.col_name')" ></Column>
      
      <Column field="companyName" :header="$t('service.appliances.col_companyName')" ></Column>

      <Column field="plans" :header="$t('service.appliances.col_services')" class="p-p-1">
        <template #body="slotProps">
          <div>
            <div v-if="hasPlanActive(slotProps.data, 'PLUS')" class="plan-badge">
              <div class="plan-plus">
                <img src="/assets/irinox/icons/plans/plus.svg" :alt="$t('service.appliances.table_badge_active_plus')" />
              </div>
              <span>{{ getPlanExpireDate(slotProps.data, 'PLUS') }}</span>
            </div>
            <div v-else-if="hasPlanActive(slotProps.data, 'PREMIUM')" class="plan-badge">
              <div class="plan-premium">
                <img src="/assets/irinox/icons/plans/premium.svg" :alt="$t('service.appliances.table_badge_active_premium')" />
              </div>
              <span>{{ getPlanExpireDate(slotProps.data, 'PREMIUM') }}</span>
            </div>
            <div v-else-if="hasPlanActivable(slotProps.data, 'PLUS') && !hasPlanActivable(slotProps.data, 'PREMIUM') && !hasPlanActive(slotProps.data, 'PREMIUM')" class="plan-badge">
              <div class="plan-plus small">{{$t('service.appliances.table_badge_activable_plus')}}</div>
            </div>
            <div v-else-if="hasPlanActivable(slotProps.data, 'PREMIUM') && !hasPlanActive(slotProps.data, 'PLUS')" class="plan-badge">
              <div class="plan-premium small">{{$t('service.appliances.table_badge_activable_premium')}}</div>
            </div>
            <div v-if="hasPlanActive(slotProps.data, 'GELATERIA')" class="plan-badge">
              <div class="plan-gelateria">
                <img src="/assets/irinox/icons/plans/gelateria.svg" :alt="$t('service.appliances.table_badge_active_gelateria')" />
              </div>
              <span>{{ getPlanExpireDate(slotProps.data, 'GELATERIA') }}</span>
            </div>
            <div v-else-if="hasPlanActivable(slotProps.data, 'GELATERIA')" class="plan-badge">
              <div class="plan-gelateria small">{{$t('service.appliances.table_badge_activable_gelateria')}}</div>
            </div>
          </div>
        </template>
      </Column>

      <Column bodyClass="" class="irinox-table-buttons p-text-right">
        <template #body="slotProps">
          <div class="button-body">
            <i v-if="slotProps.data.installationReport" class="icon-report" style="font-size: 24px; cursor: pointer"
              @click="openDownload($event, slotProps.data.installationReport)"
              v-tooltip.left="$t('report.tooltip_download')" />
            <img src="assets/irinox/icons/device.svg" @click="openApplianceDetails(slotProps.data.serialNumber)" v-tooltip.left="$t('service.appliances.tooltip_details')" :alt="$t('service.appliances.tooltip_details')"/>
            <img src="assets/irinox/icons/haccp_text.svg" @click="openHaccp(slotProps.data.serialNumber)" v-tooltip.left="$t('service.appliances.tooltip_haccp')" :alt="$t('service.appliances.tooltip_haccp')"/>
          </div>
        </template>
      </Column>
    </DataTable>

    <ReportDownloadOverlay ref="downloadOverlay" />

  </div>

  <!--        -->
  <!-- ERRORS -->
  <!--        -->
  <IrinoxDialogDark :visible="dialog.error" 
    mode="error" 
    :message="$t('service.error_dialog')"
    :life="3000" 
    @update:visible="dialog.error = $event" />

</template>

<script>
import ApplianceService from '../../service/ApplianceService';
import IrinoxDialogDark from '../IrinoxDialogDark.vue';
import ReportDownloadOverlay from '../report/ReportDownloadOverlay.vue';
import {FilterMatchMode} from 'primevue/api';
import { mapGetters } from 'vuex';

export default {
    components: {
        IrinoxDialogDark,
        ReportDownloadOverlay,
    },
    data() {
        return {
          filters: {
              'global': {value: null, matchMode: FilterMatchMode.CONTAINS},
              'modelNumber': {value: null, matchMode: FilterMatchMode.EQUALS},
              'online': {value: null, matchMode: FilterMatchMode.EQUALS}
          },
          modelNumbers: [{ 
              code: null,
              name: ''
            }],
          statusButton: false,
          dialog: {
            error: false,
          },
          table: {
            rows: 10,
            totalRecords: 0,
            first: 0
          },
          freeSearchTimer: null,
        };
    },
    computed: {
        ...mapGetters({
          personaltoken: 'userdata/personaltoken',
          appliances: 'appliances/appliances',
          uxData: 'preferences/uxData',
        }),
    },
    watch: {
    },
    created() {
      this.applianceService = new ApplianceService();
    },
    mounted() {
      this.modelNumbers = [{
        code: null,
        name: this.filterRemoveLabel(),
      }];
      this.filters = {...this.filters, ...(this.$functions.clone(this.uxData['service.appliances.filters'] ?? {}))};
      if (this.filters['online'].value) {
        this.statusButton = true;
      } else {
        this.statusButton = false;
      }
      this.table.first = this.uxData['service.appliances.paginationFirst'] ?? 0;
      this.reloadAppliances();
      this.reloadModelFilters();
    },
    methods: {
      filterRemoveLabel() {
        return typeof this.$t === 'function'
          ? this.$t('service.appliances.filter_remove')
          : 'Remove';
      },
      reloadModelFilters() {
        let mns = [];
        this.applianceService.getServiceModelsForFilter(this.personaltoken)
          .then((models) => {
            for (const model of models) {
              mns.push({
                code: model.gamma + "|||" + model.modelName,
                name: this.$functions.fixModelName(model.gamma + " " + model.modelName),
              });
            }
            this.modelNumbers = [
              { code: null, name: this.filterRemoveLabel() },
              ...this.$functions.uniqByKeepFirst(mns, item => item.code).sort((a, b) => this.$functions.compareStrings(a, b, 'name')),
            ];
          })
          .catch((error) => {
            console.error("FILTER MODELS ERROR", error);
          });
      },
      freeSearch() {
        clearTimeout(this.freeSearchTimer);
        this.freeSearchTimer = setTimeout(() => {
          this.changeFilters();
        }, 1000);
      },
      changeFilters() {
        if (this.statusButton) {
          this.filters['online'].value = true;
        } else {
          this.filters['online'].value = null;
        }
        this.$store.commit('preferences/addUxData', {'service.appliances.filters': this.$functions.clone(this.filters)});
        this.table.first = 0;
        this.reloadAppliances();
      },
      reloadAppliances() {
        this.applianceService.getServiceAppliancesFilter(this.personaltoken, this.filters, this.table)
          .then((data) => {
            if(data.code==200){
              const response = data.body;
              this.table.totalRecords = response.count;
              this.$store.commit('appliances/appliances', response.appliances);
              this.$store.commit('preferences/addUxData', {'service.appliances.paginationFirst': this.table.first});
            } else {
              console.error("ERROR "+data);
              this.table.totalRecords = 0;
              this.$store.commit('appliances/appliances', []);
              this.dialog.error = true;
            }
          })
          .catch(error => {
            console.error("EXCEPTION "+error);
            this.table.totalRecords = 0;
            this.$store.commit('appliances/appliances', []);
            this.dialog.error = true;
          });
      },
      openDownload(event, report) {
        this.$refs.downloadOverlay.open(event, report.serialNumber, report.installationReportId, report.language);
      },
      openApplianceDetails(serialNumber) {
        this.$router.push({ name: 'appliance-details', params: { serialNumber: serialNumber } });
      },
      openHaccp(serialNumber) {
        this.$router.push({ name: 'haccp-reports-historyback', params: { serialNumber: serialNumber }});
      },
      hasPlanActive(appliance, planCode) {
        return appliance?.plans?.some(plan => plan.code === planCode && plan.startDate !=null);
      },
      hasPlanActivable(appliance, planCode) {
        return appliance?.plans?.some(plan => plan.code === planCode && plan.startDate == null);
      },
      getPlanExpireDate(appliance, planCode) {
        const plan = appliance?.plans?.find(plan => plan.code === planCode);
        return plan ? this.$d(plan.endDate) : null;
      },
    }
}
</script>

<style scoped>

.irinox-form .p-dropdown {
  width: 200px;
}

</style>

<style>


</style>